import type {QueryClient} from "@tanstack/react-query";

import {applyActiveShiftPrimaryProperty, markPendingActiveShiftPrimaryPropertySync} from "@/src/modules/core/lib/apply-active-shift-primary-property";
import {useMeStore} from "@/src/modules/core/store/me";
import {shiftsService} from "@/src/modules/shifts/services";
import {useShiftsStore} from "@/src/modules/shifts/store";
import type {Shift, WorkedShift} from "@/src/modules/shifts/types";

import type {ShiftStartResponseData} from "../types";

export function buildActiveShiftFromStart(
  scheduled: Shift,
  response: ShiftStartResponseData,
): Shift {
  const workedRaw = response.worked_shift;
  const workedFromResponse =
    workedRaw != null && typeof workedRaw === "object" ? (workedRaw as WorkedShift) : null;
  const baseWorked = workedFromResponse ?? scheduled.worked_shift;

  const actualStart =
    response.actual_start_datetime ?? baseWorked?.actual_start_datetime ?? "";
  const workedShift: WorkedShift = {
    ...baseWorked,
    worked_shift_id: response.worked_shift_id ?? baseWorked?.worked_shift_id,
    actual_start_datetime: actualStart,
    actual_end_datetime: baseWorked?.actual_end_datetime ?? null,
  };

  return {
    ...scheduled,
    shift_id: response.shift_id,
    status: "active",
    worked_shift_id: response.worked_shift_id ?? scheduled.worked_shift_id,
    worked_shift: workedShift,
  };
}

export function setActiveShiftQueryCache(queryClient: QueryClient, shift: Shift) {
  queryClient.setQueryData<{shift: Shift | null}>(["active-shift"], {shift});
  useShiftsStore.setState({activeShift: shift});
}

/** After POST start 200: seed active cache, refetch authoritative row, then refresh upcoming. */
export async function syncAfterShiftStartSuccess(
  queryClient: QueryClient,
  scheduled: Shift,
  response: ShiftStartResponseData,
  invalidateShifts: () => void,
) {
  const active = buildActiveShiftFromStart(scheduled, response);
  setActiveShiftQueryCache(queryClient, active);
  markPendingActiveShiftPrimaryPropertySync(active.shift_id);
  const meProperties = useMeStore.getState().me?.properties ?? [];
  if (meProperties.length > 0) {
    applyActiveShiftPrimaryProperty(active, meProperties);
  }
  await queryClient.refetchQueries({queryKey: ["active-shift"]});
  invalidateShifts();
  void queryClient.refetchQueries({queryKey: ["briefingboard-pending"], type: "all"});
}

/** After 400 already-started: load active from API if it matches the shift the user tried to start. */
export async function recoverAlreadyStartedShift(
  queryClient: QueryClient,
  shiftId: number,
): Promise<boolean> {
  const {shift} = await shiftsService.getActiveShift();
  if (!shift || shift.shift_id !== shiftId) {
    return false;
  }
  setActiveShiftQueryCache(queryClient, shift);
  markPendingActiveShiftPrimaryPropertySync(shift.shift_id);
  const meProperties = useMeStore.getState().me?.properties ?? [];
  if (meProperties.length > 0) {
    applyActiveShiftPrimaryProperty(shift, meProperties);
  }
  void queryClient.refetchQueries({queryKey: ["briefingboard-pending"], type: "all"});
  return true;
}
